import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Card,
  CardHeader,
  CardBody,
  Row,
  Col,
  Input,
  Button,
  Table,
  Modal,
  ModalBody,
} from "reactstrap";
import { ClipLoader } from "react-spinners";
import { useHistory, useLocation } from "react-router-dom";
import api from "../../../services/api";
import { useCompanies } from "../../../hooks/companies";
import XmlViewerModal from "../../../components/autopilot/XmlViewerModal";
import TxtViewerModal from "../../../components/autopilot/TxtViewerModal";
import PayloadViewerModal from "../../../components/autopilot/PayloadViewerModal";
import ChatModal from "../../../components/autopilot/ChatModal";
import autopilotMiniIcon from "../../../assets/img/autopilot-mini-icon.png";
import ImportacaoArvore from "./ImportacaoArvore";
import AvisoForaDoFiltro from "../../../components/AvisoForaDoFiltro";
import LupaInconsistencias from "./lupa/LupaInconsistencias";
import {
  AUTOPILOT_COLORS,
  autopilotBodyStyle,
  autopilotCardStyle,
  autopilotDescriptionStyle,
  autopilotEyebrowStyle,
  autopilotHeaderStyle,
  autopilotSecondaryButtonStyle,
  autopilotTitleStyle,
} from "../../../components/autopilot/styles";

// PO1-3055: teto por chamada, NUNCA global (um default global cortaria telas
// de exportação legitimamente lentas — ver comentário em services/api.js).
// A varredura do SFTP agora tem corte no backend em poucos segundos; 45s é
// folga para o pior caso com o SFTP saudável.
const TIMEOUT_FILA_MS = 45000;

const PAGE_SIZES = [10, 20, 50, 100];
const DEFAULT_NF_HOURS = 168;
const NF_WINDOWS = [
  { value: 24, label: "Últimas 24h" },
  { value: 72, label: "Últimos 3 dias" },
  { value: 168, label: "Últimos 7 dias" },
  { value: 336, label: "Últimos 14 dias" },
];

const TAB_OPTIONS = [
  {
    key: "nf",
    label: "NF-e",
    title: "NF-es com erro",
    helper: "Notas retidas nos logs de integração.",
    iconClassName: "tim-icons icon-alert-circle-exc",
    tone: "warning",
  },
  {
    key: "cte",
    label: "CT-e",
    title: "CT-es pendentes",
    helper: "XMLs recebidos que ainda não entraram no TMS.",
    iconClassName: "tim-icons icon-delivery-fast",
    tone: "info",
  },
  {
    key: "fatura",
    label: "Fatura",
    title: "Faturas pendentes",
    helper: "Arquivos de cobrança aguardando tratamento.",
    iconClassName: "tim-icons icon-notes",
    tone: "success",
  },
];

const TONE_COLORS = {
  info: AUTOPILOT_COLORS.statusInfo,
  success: AUTOPILOT_COLORS.statusSuccess,
  warning: AUTOPILOT_COLORS.statusWarning,
  danger: AUTOPILOT_COLORS.statusDanger,
  accent: AUTOPILOT_COLORS.accent,
  neutral: AUTOPILOT_COLORS.muted,
};

const heroHeaderStyle = { ...autopilotHeaderStyle, padding: "1.35rem 1.45rem 1.25rem" };
const heroTitleStyle = { ...autopilotTitleStyle, margin: "0 0 0.4rem", fontSize: "1.75rem", fontWeight: 800, lineHeight: 1.15 };
const heroDescriptionStyle = { ...autopilotDescriptionStyle, maxWidth: 820, marginBottom: 0, fontSize: "0.92rem", lineHeight: 1.55 };

const heroActionsStyle = {
  display: "flex",
  flexWrap: "wrap",
  justifyContent: "flex-end",
  gap: "0.5rem",
};

const queueTabsGridStyle = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(210px, 1fr))",
  gap: "0.75rem",
  marginBottom: "0.9rem",
};

const sectionCardStyle = {
  ...autopilotCardStyle,
  marginTop: "1rem",
  background: "var(--everlog-card-hero-bg)",
};

const panelHeaderStyle = {
  display: "flex",
  alignItems: "flex-start",
  justifyContent: "space-between",
  gap: "0.75rem",
  marginBottom: "0.85rem",
};

const panelTitleStyle = {
  color: AUTOPILOT_COLORS.title,
  fontSize: "0.95rem",
  fontWeight: 700,
  marginBottom: "0.2rem",
};

const panelDescriptionStyle = {
  color: AUTOPILOT_COLORS.muted,
  fontSize: "0.8rem",
  lineHeight: 1.45,
};

const insightGridStyle = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))",
  gap: "0.65rem",
  marginBottom: "1rem",
};

const tableHeaderStyle = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: "0.75rem",
  marginBottom: "0.7rem",
};

const selectionToolbarStyle = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  flexWrap: "wrap",
  gap: "0.75rem",
  border: `1px solid ${AUTOPILOT_COLORS.border}`,
  borderRadius: 12,
  background: AUTOPILOT_COLORS.insetSurfaceActive,
  padding: "0.75rem 0.85rem",
  marginBottom: "0.9rem",
};

const expandButtonStyle = {
  width: 28,
  height: 28,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  borderRadius: 8,
  border: `1px solid ${AUTOPILOT_COLORS.border}`,
  background: "transparent",
  color: AUTOPILOT_COLORS.muted,
  cursor: "pointer",
};

const filterLabelStyle = {
  fontSize: "0.68rem",
  fontWeight: 700,
  letterSpacing: "0.08em",
  textTransform: "uppercase",
  color: AUTOPILOT_COLORS.muted,
  marginBottom: "0.3rem",
  display: "block",
};

const thStyle = {
  fontSize: "0.7rem",
  fontWeight: 700,
  letterSpacing: "0.06em",
  textTransform: "uppercase",
  color: AUTOPILOT_COLORS.muted,
  borderBottom: `1px solid ${AUTOPILOT_COLORS.dividerStrong}`,
  whiteSpace: "nowrap",
};

const tdStyle = {
  fontSize: "0.82rem",
  color: AUTOPILOT_COLORS.text,
  borderTop: `1px solid ${AUTOPILOT_COLORS.divider}`,
  verticalAlign: "middle",
};

const smallActionButtonStyle = {
  background: AUTOPILOT_COLORS.infoButtonBg,
  border: `1px solid ${AUTOPILOT_COLORS.infoButtonBorder}`,
  borderRadius: 4,
  color: AUTOPILOT_COLORS.infoButtonText,
  fontSize: "0.72rem",
  padding: "3px 10px",
  cursor: "pointer",
};

// Ação primária por linha (Integrar CT-e/Fatura, Reprocessar NF): verde, contorno.
const smallIntegrarButtonStyle = {
  background: "transparent",
  border: `1px solid ${AUTOPILOT_COLORS.statusSuccess}`,
  borderRadius: 4,
  color: AUTOPILOT_COLORS.statusSuccess,
  fontSize: "0.72rem",
  padding: "3px 10px",
  cursor: "pointer",
};

// Botão Autopilot por linha: abre o chat do Autopilot com o contexto do documento.
// Mesmo visual do monitor (roxo + robozinho).
const smallAutopilotButtonStyle = {
  background: AUTOPILOT_COLORS.purpleBg,
  border: `1px solid ${AUTOPILOT_COLORS.purpleBorder}`,
  borderRadius: 4,
  color: AUTOPILOT_COLORS.purpleText,
  fontSize: "0.72rem",
  padding: "3px 10px",
  cursor: "pointer",
  display: "inline-flex",
  alignItems: "center",
  gap: "4px",
};

// Botão Rejeitar por linha (NF no SFTP): contorno vermelho.
const smallRejeitarButtonStyle = {
  background: "transparent",
  border: `1px solid ${AUTOPILOT_COLORS.statusDanger}`,
  borderRadius: 4,
  color: AUTOPILOT_COLORS.statusDanger,
  fontSize: "0.72rem",
  padding: "3px 10px",
  cursor: "pointer",
};

function parseDateValue(value) {
  if (!value) return null;
  const d = new Date(value);
  if (!Number.isNaN(d.getTime())) return d;
  // Dat_Emissao de fatura é varchar ("2026-04-13 21:00:00.000 -03:00")
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(value));
  if (m) return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return null;
}

function formatDate(value) {
  const d = parseDateValue(value);
  return d ? d.toLocaleDateString("pt-BR") : "-";
}

function formatDateTime(value) {
  const d = parseDateValue(value);
  if (!d) return "-";
  return `${d.toLocaleDateString("pt-BR")} ${d.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })}`;
}

function formatBytes(value) {
  if (value == null || Number.isNaN(Number(value))) return "-";
  const n = Number(value);
  if (n < 1024) return n + " B";
  if (n < 1024 * 1024) return (n / 1024).toFixed(1) + " KB";
  return (n / (1024 * 1024)).toFixed(1) + " MB";
}

function formatRelative(value) {
  const d = parseDateValue(value);
  if (!d) return "-";
  const diffMs = Date.now() - d.getTime();
  const diffH = Math.floor(diffMs / 36e5);
  if (diffH < 1) return "há menos de 1h";
  if (diffH < 48) return `há ${diffH}h`;
  const diffD = Math.floor(diffH / 24);
  if (diffD < 60) return `há ${diffD} dias`;
  return `há ${Math.floor(diffD / 30)} meses`;
}

function formatCurrency(value) {
  if (value == null || value === "" || Number.isNaN(Number(value))) return "-";
  return Number(value).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
}

function formatCount(value) {
  if (value == null || value === "") return "-";
  return Number(value || 0).toLocaleString("pt-BR");
}

function getToneColor(tone) {
  return TONE_COLORS[tone] || AUTOPILOT_COLORS.accent;
}

// --- CT-e: recorte da fila NO CLIENTE (espelha o que o backend fazia em SQL) ---
// A fila completa do escopo vem num único fetch; transportadora/categoria (árvore),
// busca, tipo de pendência (cards), ordenação e paginação são aplicados aqui.
const SORT_FIELD_FILA = {
  dataEmissao: "Dat_Emissao",
  arquivo: "NomeArquivo",
  transportadora: "Fantasia",
  numero: "Numero",
  criadoEm: "CreationTime",
  embarcador: "Embarcador",
};
function ordenarFilaClient(arr, sort, order) {
  const campo = SORT_FIELD_FILA[sort] || SORT_FIELD_FILA.dataEmissao;
  const dir = String(order).toUpperCase() === "DESC" ? -1 : 1;
  const ehData = campo === "Dat_Emissao" || campo === "CreationTime";
  return arr.slice().sort((a, b) => {
    let va = a[campo];
    let vb = b[campo];
    if (ehData) {
      va = va ? new Date(va).getTime() : 0;
      vb = vb ? new Date(vb).getTime() : 0;
    } else {
      va = (va == null ? "" : String(va)).toLowerCase();
      vb = (vb == null ? "" : String(vb)).toLowerCase();
    }
    if (va < vb) return -1 * dir;
    if (va > vb) return 1 * dir;
    const na = (a.NomeArquivo || "").toLowerCase();
    const nb = (b.NomeArquivo || "").toLowerCase();
    return na < nb ? -1 : na > nb ? 1 : 0;
  });
}
// PO1-3409: documentos da fila já carregada que batem com a busca mas estão
// escondidos pelos recortes locais (árvore, pendência, datas, Lupa).
function contarOcultosPelosFiltros({ isTree, search, filaView, filaCompleta }) {
  if (!isTree || !search || (filaView && filaView.length)) return 0;
  return recortarFilaClient(filaCompleta, { search }).length;
}

// PO1-3409: vazio da grade. Sem busca, o texto de sempre; com busca, diz se o
// documento está só escondido pelos filtros locais ou, perguntando ao servidor,
// se existe fora do escopo da tela (já integrado, outro embarcador/transportadora).
// Fica fora do componente da tela de propósito: o lint de hooks antigo estoura a
// contagem de caminhos num componente desse tamanho.
function VazioGradeFila({ isTree, tipo, search, ocultosPelosFiltros, onMostrarSemFiltros, contexto }) {
  const vazio = "Nenhuma pendência com os filtros atuais.";
  if (!isTree || !search) return vazio;
  if (ocultosPelosFiltros > 0) {
    return (
      <div role="status">
        {`${ocultosPelosFiltros} documento(s) da fila batem com "${search}", mas estão escondidos pelos filtros da tela (transportadora, categoria, pendência, datas ou Lupa).`}
        <div style={{ marginTop: "0.6rem" }}>
          <Button size="sm" outline color="warning" onClick={onMostrarSemFiltros}>
            Mostrar sem esses filtros
          </Button>
        </div>
      </div>
    );
  }
  return <AvisoForaDoFiltro tipo={tipo} tela="integracao" termo={search} contexto={contexto} fallback={vazio} />;
}

function recortarFilaClient(fila, { idTransportadora, categoria, search, pendencia, dataInicio, dataFim, dataBase, sort, order, motivo, motivoCte, idEmbarcador }) {
  let out = fila || [];
  // PO1-3325: recorte vindo da Lupa (embarcador na visão todos, motivo do
  // documento e, na fatura, motivo do CT-e que a segura).
  if (idEmbarcador) {
    out = out.filter((r) => Number(r.idEmbarcador) === Number(idEmbarcador));
  }
  if (motivo) {
    out = out.filter((r) => (r.motivos || []).some((m) => m.motivo === motivo));
  }
  if (motivoCte) {
    out = out.filter((r) => (r.ctes || []).some((c) => (c.motivos || []).some((m) => m.motivo === motivoCte)));
  }
  if (idTransportadora) {
    const t = Number(idTransportadora);
    // Filtra pelo GRUPO (matriz) quando a linha traz idTransportadoraGrupo
    // (Fatura, via de-para — ex.: Chibatão agrega JF Navegação); senão pela
    // própria transportadora. Espelha o construirArvore/_coletarFila do backend.
    out = out.filter((r) => Number(r.idTransportadoraGrupo != null ? r.idTransportadoraGrupo : r.idTransportadora) === t);
  }
  if (categoria && categoria !== "all") {
    out = out.filter((r) => (r.categorias || []).indexOf(categoria) !== -1);
  }
  if (search) {
    const q = String(search).toLowerCase();
    out = out.filter(
      (r) =>
        (r.NomeArquivo || "").toLowerCase().indexOf(q) !== -1 ||
        String(r.Numero || "").toLowerCase().indexOf(q) !== -1 ||
        (r.Fantasia || "").toLowerCase().indexOf(q) !== -1
    );
  }
  if (pendencia) {
    out = out.filter((r) => r.tipoPendencia === pendencia);
  }
  // Janela de datas também no cliente, para ficar instantânea. PO1-2809: a
  // data de negócio (emissão/vencimento) pode ser nula (ex.: fatura recebida
  // mas ainda não processada) — nesse caso a linha fica SEMPRE visível, em
  // vez de ser excluída como acontecia antes com CreationTime fixo.
  const campoDataBase = dataBase === "vencimento" ? "Dat_VencimentoExterna" : "Dat_Emissao";
  if (dataInicio) {
    const ini = new Date(dataInicio + "T00:00:00");
    out = out.filter((r) => {
      const valor = r[campoDataBase];
      if (!valor) return true;
      return new Date(valor) >= ini;
    });
  }
  if (dataFim) {
    const fim = new Date(dataFim + "T00:00:00");
    fim.setDate(fim.getDate() + 1);
    out = out.filter((r) => {
      const valor = r[campoDataBase];
      if (!valor) return true;
      return new Date(valor) < fim;
    });
  }
  return ordenarFilaClient(out, sort, order);
}

function QueueTabCard({ option, active, count, onClick }) {
  const color = getToneColor(option.tone);
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      style={{
        width: "100%",
        minHeight: 112,
        display: "flex",
        flexDirection: "column",
        alignItems: "stretch",
        justifyContent: "space-between",
        borderRadius: 14,
        border: `1px solid ${active ? color : AUTOPILOT_COLORS.border}`,
        background: active ? AUTOPILOT_COLORS.insetSurfaceActive : AUTOPILOT_COLORS.insetSurface,
        color: AUTOPILOT_COLORS.text,
        padding: "0.9rem 0.95rem",
        textAlign: "left",
        cursor: "pointer",
        boxShadow: active ? `inset 0 0 0 1px ${color}` : "none",
      }}
    >
      <span style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "0.7rem" }}>
        <span style={{ color: AUTOPILOT_COLORS.title, fontSize: "0.92rem", fontWeight: 700 }}>{option.title}</span>
        <span
          style={{
            width: 32,
            height: 32,
            borderRadius: 10,
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            color,
            background: "var(--everlog-card-surface-soft, rgba(255,255,255,0.04))",
            border: `1px solid ${AUTOPILOT_COLORS.border}`,
            flexShrink: 0,
          }}
        >
          <i className={option.iconClassName} />
        </span>
      </span>
      <span style={{ display: "block", color, fontSize: "1.35rem", fontWeight: 800, lineHeight: 1.1, marginTop: "0.65rem" }}>
        {formatCount(count)}
      </span>
      <span style={{ color: AUTOPILOT_COLORS.muted, fontSize: "0.76rem", lineHeight: 1.35, marginTop: "0.35rem" }}>
        {option.helper}
      </span>
    </button>
  );
}

function InsightTile({ label, value, helper, tone, iconClassName, loading, onClick, active }) {
  const color = getToneColor(tone);
  const clickable = typeof onClick === "function";
  return (
    <div
      onClick={onClick}
      role={clickable ? "button" : undefined}
      tabIndex={clickable ? 0 : undefined}
      aria-pressed={clickable ? !!active : undefined}
      onKeyDown={
        clickable
          ? (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onClick();
              }
            }
          : undefined
      }
      style={{
        border: `1px solid ${active ? color : AUTOPILOT_COLORS.border}`,
        borderLeft: `3px solid ${color}`,
        borderRadius: 12,
        background: active ? AUTOPILOT_COLORS.insetSurfaceActive : AUTOPILOT_COLORS.insetSurface,
        padding: "0.75rem 0.8rem",
        minHeight: 86,
        cursor: clickable ? "pointer" : "default",
        boxShadow: active ? `inset 0 0 0 1px ${color}` : "none",
        transition: "background 120ms ease, border-color 120ms ease",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "0.6rem", marginBottom: "0.45rem" }}>
        <span style={{ color: AUTOPILOT_COLORS.muted, fontSize: "0.68rem", fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase" }}>
          {label}
        </span>
        {iconClassName && <i className={iconClassName} style={{ color, fontSize: "0.92rem" }} />}
      </div>
      <div style={{ color: AUTOPILOT_COLORS.title, fontSize: "1.08rem", fontWeight: 800, lineHeight: 1.2 }}>
        {loading ? "-" : value}
      </div>
      <div style={{ color: AUTOPILOT_COLORS.muted, fontSize: "0.74rem", marginTop: "0.25rem" }}>{helper}</div>
    </div>
  );
}

function PendenciaBadge({ tipo }) {
  // Três estados: ação necessária (vermelho), aguardando dependência (laranja),
  // recebido/não processado (azul = está no SFTP mas ainda não virou Work_Cte).
  const variantes = {
    naoProcessado: {
      label: "Recebido, não processado",
      bg: "var(--everlog-status-info-bg, rgba(54,163,255,0.12))",
      border: "var(--everlog-status-info-border, rgba(54,163,255,0.35))",
      color: AUTOPILOT_COLORS.accent,
      title: "Arquivo na origem do SFTP que ainda não entrou no sistema (sem Work_Cte). Pode ser integrado ou rejeitado.",
    },
    dependencia: {
      label: "Aguardando dependência",
      bg: "var(--everlog-status-warning-bg, rgba(255,178,54,0.12))",
      border: "var(--everlog-status-warning-border, rgba(255,178,54,0.35))",
      color: AUTOPILOT_COLORS.statusWarning,
      title: "Aguardando outro documento chegar (NF/CT-e). O reprocessamento é automático e contínuo; normalmente não requer ação.",
    },
    acionavel: {
      label: "Ação necessária",
      bg: AUTOPILOT_COLORS.statusDangerBg,
      border: AUTOPILOT_COLORS.statusDangerBorder,
      color: AUTOPILOT_COLORS.statusDanger,
      title: "Pendência que requer ação (cadastro, correção do arquivo ou rejeição).",
    },
  };
  const v = variantes[tipo] || variantes.acionavel;
  return (
    <span
      style={{
        display: "inline-block",
        padding: "2px 8px",
        borderRadius: 10,
        fontSize: "0.68rem",
        fontWeight: 700,
        whiteSpace: "nowrap",
        background: v.bg,
        border: `1px solid ${v.border}`,
        color: v.color,
      }}
      title={v.title}
    >
      {v.label}
    </span>
  );
}

function ErrorTypeBadge({ tipo }) {
  const isBiz = tipo === "business-error";
  return (
    <span
      style={{
        display: "inline-block",
        padding: "2px 8px",
        borderRadius: 10,
        fontSize: "0.68rem",
        fontWeight: 700,
        whiteSpace: "nowrap",
        background: isBiz ? "var(--everlog-status-warning-bg, rgba(255,178,54,0.12))" : AUTOPILOT_COLORS.statusDangerBg,
        border: `1px solid ${isBiz ? "var(--everlog-status-warning-border, rgba(255,178,54,0.35))" : AUTOPILOT_COLORS.statusDangerBorder}`,
        color: isBiz ? AUTOPILOT_COLORS.statusWarning : AUTOPILOT_COLORS.statusDanger,
      }}
    >
      {isBiz ? "Negócio" : tipo === "technical-error" ? "Técnico" : tipo || "-"}
    </span>
  );
}

function ModalHeaderBar({ title, onClose }) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "1rem 1.25rem",
        borderBottom: `1px solid ${AUTOPILOT_COLORS.divider}`,
      }}
    >
      <strong style={{ color: AUTOPILOT_COLORS.title, fontSize: "1rem" }}>{title}</strong>
      <button
        type="button"
        onClick={onClose}
        style={{ background: "transparent", border: "none", color: AUTOPILOT_COLORS.muted, fontSize: "1.2rem", cursor: "pointer", lineHeight: 1 }}
        aria-label="Fechar"
      >
        ×
      </button>
    </div>
  );
}

// PO1-3055: avisos da leitura do SFTP, acima dos cards. Componente próprio (e
// não JSX inline) porque o Importacao já é grande demais: dois blocos
// condicionais a mais faziam a regra react-hooks/rules-of-hooks marcar hooks
// legítimos como condicionais e o build de produção falhava.
function AvisoLeituraSftp({ sftpIndisponivel, canaisComFalha }) {
  if (sftpIndisponivel) {
    return (
      <div
        style={{
          padding: "0.6rem 0.9rem",
          marginBottom: "0.7rem",
          borderRadius: 6,
          background: AUTOPILOT_COLORS.statusDangerBg,
          border: `1px solid ${AUTOPILOT_COLORS.statusDangerBorder}`,
          color: AUTOPILOT_COLORS.statusDanger,
          fontSize: "0.82rem",
        }}
      >
        <strong>SFTP indisponível.</strong> Não foi possível ler os arquivos na
        origem, então os números abaixo <strong>não refletem</strong> o que está
        pendente — não são zero de verdade. As pendências já registradas
        continuam corretas.
      </div>
    );
  }

  const falhas = canaisComFalha || [];
  if (falhas.length === 0) return null;

  const amostra = falhas.slice(0, 3).map((c) => c.fantasia || c.canalId).join(", ");
  return (
    <div
      style={{
        padding: "0.6rem 0.9rem",
        marginBottom: "0.7rem",
        borderRadius: 6,
        background: "var(--everlog-status-warning-bg, rgba(255,178,54,0.12))",
        border: "1px solid var(--everlog-status-warning-border, rgba(255,178,54,0.35))",
        fontSize: "0.82rem",
      }}
    >
      {falhas.length === 1
        ? "1 canal não respondeu nesta leitura"
        : `${falhas.length} canais não responderam nesta leitura`}{" "}
      ({amostra}
      {falhas.length > 3 ? "…" : ""}). Os números abaixo podem estar incompletos.
    </div>
  );
}

// PO1-3055: por padrão a tela lista só os canais com vínculo ativo e recepção
// nos últimos 90 dias (na Hengst, 321 -> 29 canais de CT-e, que é o que faz a
// tela carregar em segundos). Canal quieto não some do sistema: a ingestão
// automática varre o cadastro inteiro, independentemente deste filtro.
function BarraVerTodosCanais({ verTodosCanais, onToggle }) {
  return (
    <div style={{ marginTop: "0.75rem", display: "flex", alignItems: "center", gap: "0.6rem", flexWrap: "wrap" }}>
      <button
        type="button"
        style={autopilotSecondaryButtonStyle}
        onClick={onToggle}
        title={
          verTodosCanais
            ? "Voltar a listar só os canais ativos e com recebimento recente"
            : "Listar também os canais sem recebimento nos últimos 90 dias e de vínculo encerrado"
        }
      >
        {verTodosCanais ? "Mostrar só canais ativos" : "Ver todos os canais"}
      </button>
      <span style={{ fontSize: "0.76rem", opacity: 0.75 }}>
        {verTodosCanais
          ? "Mostrando todos os canais cadastrados — a leitura fica mais lenta."
          : "Mostrando canais com vínculo ativo e recebimento nos últimos 90 dias."}
      </span>
    </div>
  );
}

// modo "lupa" = página Sustentação > Lupa de Inconsistências (PO1-3325): a mesma
// fila, a mesma grade e as mesmas ações, com a Lupa no lugar dos cards de fila e
// da triagem. A grade acompanha a bolha em foco.
const Importacao = ({ modo }) => {
  const isLupa = modo === "lupa";
  const history = useHistory();
  const location = useLocation();
  // Seletor global do header: embarcador (empresa) e transportadora valem
  // para a tela toda. Sem embarcador selecionado, a fila mostra TODOS.
  const { empresa, transportadora } = useCompanies();
  const empresaId = empresa && empresa.value;
  const headerTransportadoraId = transportadora && transportadora.value;
  const verTodos = !empresaId;

  const initialTab = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const t = params.get("tab");
    if (t === "fatura") return t;
    return t === "nf" && !isLupa ? t : "cte";
  }, [location.search, isLupa]);
  const [tab, setTab] = useState(initialTab);
  const isCte = tab === "cte";
  const isNf = tab === "nf";
  // CT-e e Fatura usam a árvore de triagem (master-detail). NF segue lista plana.
  const isTree = !isNf;

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [resumo, setResumo] = useState(null);
  const [loading, setLoading] = useState(false);
  // Árvore de triagem (CT-e/Fatura): master Transportadora → categoria.
  // selectedKey null = nada selecionado (a grade mostra o placeholder).
  const [arvore, setArvore] = useState([]);
  const [arvoreLoading, setArvoreLoading] = useState(false);
  // CT-e: fila completa do escopo (1 fetch ao vivo). A grade filtra/ordena/pagina
  // NO CLIENTE sobre isto — trocar card/transportadora/busca não re-lista o SFTP.
  const [filaCompleta, setFilaCompleta] = useState([]);
  // PO1-3325: catálogo de motivos que vem com a fila (Lupa) e o recorte que a
  // Lupa aplica na grade.
  const [catalogo, setCatalogo] = useState({});
  const [motivo, setMotivo] = useState("");
  const [motivoCte, setMotivoCte] = useState("");
  const [embarcadorRecorte, setEmbarcadorRecorte] = useState("");
  // PO1-3055: estado do SFTP. Antes, erro e "fila vazia" chegavam iguais aqui
  // (array vazio) e os cards mostravam 0 — o zero falso do incidente de 10/09.
  const [sftpIndisponivel, setSftpIndisponivel] = useState(false);
  const [canaisComFalha, setCanaisComFalha] = useState([]);
  // "Ver todos" desliga os filtros de vínculo ativo + recepção em 90 dias.
  const [verTodosCanais, setVerTodosCanais] = useState(false);
  const [expandedTree, setExpandedTree] = useState(() => new Set());
  const [selectedKey, setSelectedKey] = useState(null);
  const [fetchError, setFetchError] = useState(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [sort, setSort] = useState("dataEmissao");
  const [order, setOrder] = useState("asc");

  const [search, setSearch] = useState("");
  const [searchDraft, setSearchDraft] = useState("");
  const [idTransportadora, setIdTransportadora] = useState("");
  const [categoria, setCategoria] = useState("all");
  // Filtro acionável/dependência vindo dos cards (só CT-e/Fatura). NF usa nfErrorType.
  const [pendencia, setPendencia] = useState(null);
  const [dataInicio, setDataInicio] = useState("");
  const [dataFim, setDataFim] = useState("");
  // PO1-2809: qual data o filtro "Na fila desde" usa — emissão (padrão) ou
  // vencimento. Só se aplica ao ramo não-NF (fatura/árvore).
  const [dataBase, setDataBase] = useState("emissao");
  const [nfHours, setNfHours] = useState(DEFAULT_NF_HOURS);
  const [nfErrorType, setNfErrorType] = useState("");
  // Filtros avançados (período / janela / tipo de erro) colapsados por padrão.
  const [mostrarFiltros, setMostrarFiltros] = useState(false);

  const [selected, setSelected] = useState(() => new Set());
  // Mapa chave→linha das selecionadas, para montar o payload das ações (os CT-e
  // "não processado" não têm idWorkCte, então precisamos do descritor do arquivo).
  const [selectedRows, setSelectedRows] = useState(() => new Map());
  const [expanded, setExpanded] = useState(() => new Set());
  const [counts, setCounts] = useState({ cte: null, fatura: null, nf: null });
  const [exporting, setExporting] = useState(false);

  const [viewer, setViewer] = useState(null); // { tipo, nomeArquivo, idTransportadora, idEmbarcador } | { tipo: 'nf', chaveacesso, numero, idEmbarcador }
  const [confirmAction, setConfirmAction] = useState(null); // { acao: 'integrar'|'rejeitar'|'reprocessar' }
  const [comentario, setComentario] = useState("");
  const [actionLoading, setActionLoading] = useState(false);
  const [actionResult, setActionResult] = useState(null);
  // Linha em ação por botão direto (Integrar/Reprocessar) — desabilita só aquele botão.
  const [rowActionId, setRowActionId] = useState(null);
  // Chat do Autopilot aberto a partir de uma linha (contexto do documento).
  const [chatModal, setChatModal] = useState({ open: false, initialMessage: null, title: null });
  // NF-e via SFTP (clientes como Blue Bay): sub-modo da aba NF — erros do Loki x
  // fila do SFTP (arquivos recebidos ainda não processados pelo job nfeFtpToApi).
  const [nfSubview, setNfSubview] = useState("erros");
  const [nfSftpRows, setNfSftpRows] = useState([]);
  const [nfSftpLoading, setNfSftpLoading] = useState(false);
  const [nfSftpError, setNfSftpError] = useState(null);
  const [nfSftpActionFile, setNfSftpActionFile] = useState(null);
  const [processandoNf, setProcessandoNf] = useState(false);
  const nfSftpMode = isNf && nfSubview === "sftp";

  // CT-e e Fatura usam a chave unificada `id` (wc:/wf:/np:), porque os "Recebido,
  // não processado" não têm idWork*. NF segue a chave de acesso.
  const idKey = isNf ? "chaveacesso" : "id";
  // Transportadora do header tem precedência sobre o filtro local da tela
  const transportadoraEfetiva = headerTransportadoraId || idTransportadora;
  const currentTab = TAB_OPTIONS.find((option) => option.key === tab) || TAB_OPTIONS[0];

  // CT-e e Fatura: a grade é derivada NO CLIENTE da fila completa (filaCompleta).
  // O recorte (transportadora/categoria da árvore + busca + tipo de pendência) e a
  // ordenação são instantâneos; a paginação é fatiada aqui. NF segue com rows/total
  // vindos paginados do servidor.
  const filaView = useMemo(() => {
    if (!isTree) return null;
    return recortarFilaClient(filaCompleta, { idTransportadora, categoria, search, pendencia, dataInicio, dataFim, dataBase, sort, order, motivo, motivoCte, idEmbarcador: embarcadorRecorte });
  }, [isTree, filaCompleta, idTransportadora, categoria, search, pendencia, dataInicio, dataFim, dataBase, sort, order, motivo, motivoCte, embarcadorRecorte]);
  // PO1-3409: busca sem resultado na grade. Primeiro confere a fila já carregada
  // (o documento pode estar só escondido pelo recorte da árvore, pendência, datas
  // ou Lupa); se não estiver, o AvisoForaDoFiltro pergunta ao servidor.
  const ocultosPelosFiltros = useMemo(
    () => contarOcultosPelosFiltros({ isTree, search, filaView, filaCompleta }),
    [isTree, search, filaView, filaCompleta]
  );
  const displayTotal = isTree ? (filaView ? filaView.length : 0) : total;
  const displayRows = useMemo(() => {
    if (!isTree) return rows;
    const start = (page - 1) * pageSize;
    return (filaView || []).slice(start, start + pageSize);
  }, [isTree, rows, filaView, page, pageSize]);
  // Resumo dos cards vem do servidor e cobre o FILTRO INTEIRO (não só a página),
  // então os números batem: total = acionável + dependência.
  const scopeSummary = useMemo(
    () => ({
      action: (resumo && resumo.acionavel) || 0,
      waiting: (resumo && resumo.dependencia) || 0,
      naoProcessado: (resumo && resumo.naoProcessado) || 0,
      technical: (resumo && resumo.technical) || 0,
      business: (resumo && resumo.business) || 0,
      oldest: resumo && resumo.oldest ? formatRelative(resumo.oldest) : "-",
    }),
    [resumo]
  );
  const selectedLabel = `${selected.size} selecionada${selected.size === 1 ? "" : "s"}`;

  // No modo árvore os cards são o resumo do ESCOPO INTEIRO (soma dos nós de
  // transportadora), não da seleção. Mais antiga = menor data (ISO compara ok).
  const arvoreSummary = useMemo(() => {
    let total = 0;
    let action = 0;
    let waiting = 0;
    let naoProcessado = 0;
    let oldest = null;
    arvore.forEach((t) => {
      total += t.total || 0;
      action += t.acionavel || 0;
      waiting += t.dependencia || 0;
      naoProcessado += t.naoProcessado || 0;
      if (t.oldest && (!oldest || t.oldest < oldest)) oldest = t.oldest;
    });
    return { total, action, waiting, naoProcessado, oldest: oldest ? formatRelative(oldest) : "-" };
  }, [arvore]);

  const activeFiltersCount = useMemo(() => {
    let count = 0;
    if (search) count += 1;

    if (isNf) {
      if (nfHours !== DEFAULT_NF_HOURS) count += 1;
      if (nfErrorType) count += 1;
    } else {
      // Transportadora/motivo vêm da árvore (não são "filtros" da grade aqui).
      if (dataInicio || dataFim) count += 1;
    }

    return count;
  }, [search, isNf, nfHours, nfErrorType, dataInicio, dataFim]);

  // Só os filtros AVANÇADOS (sem a busca, que fica sempre visível) — badge do botão.
  const filtrosAvancadosCount = useMemo(() => {
    if (isNf) {
      return (nfHours !== DEFAULT_NF_HOURS ? 1 : 0) + (nfErrorType ? 1 : 0);
    }
    return dataInicio || dataFim ? 1 : 0;
  }, [isNf, nfHours, nfErrorType, dataInicio, dataFim]);

  const canClearFilters = activeFiltersCount > 0 || searchDraft.trim() !== "";

  // Recorte ativo nos chips dos cards: NF usa o tipo de erro, CT-e/Fatura a pendência.
  const insightFilter = isNf ? nfErrorType || null : pendencia;

  const queueInsights = useMemo(() => {
    const scopeHelper = verTodos ? "todos os embarcadores" : "escopo selecionado";
    // O card "No filtro atual" também é o chip "todos": clicar limpa o recorte.
    const totalTile = {
      label: "No filtro atual",
      value: formatCount(displayTotal),
      helper: insightFilter ? "ver todos" : scopeHelper,
      tone: "info",
      iconClassName: "tim-icons icon-chart-bar-32",
      filterValue: null,
      clickable: true,
      active: !insightFilter,
    };
    if (isNf) {
      return [
        totalTile,
        {
          label: "Erro técnico",
          value: formatCount(scopeSummary.technical),
          helper: insightFilter === "technical-error" ? "filtrando · limpar" : "filtrar",
          tone: "danger",
          iconClassName: "tim-icons icon-settings-gear-63",
          filterValue: "technical-error",
          clickable: true,
          active: insightFilter === "technical-error",
        },
        {
          label: "Erro de negócio",
          value: formatCount(scopeSummary.business),
          helper: insightFilter === "business-error" ? "filtrando · limpar" : "filtrar",
          tone: "warning",
          iconClassName: "tim-icons icon-alert-circle-exc",
          filterValue: "business-error",
          clickable: true,
          active: insightFilter === "business-error",
        },
        {
          label: "Mais antiga",
          value: scopeSummary.oldest,
          helper: "no filtro atual",
          tone: "neutral",
          iconClassName: "tim-icons icon-time-alarm",
        },
      ];
    }

    // CT-e/Fatura (modo árvore): cards são o resumo do escopo inteiro E filtram a
    // grade por TIPO de pendência (combina com o recorte de transportadora/motivo
    // da árvore). Os números vêm da árvore (escopo cheio), por isso não mudam ao
    // filtrar — só a grade reage. "No escopo" limpa o filtro de tipo.
    const cards = [
      {
        label: "No escopo",
        value: formatCount(arvoreSummary.total),
        helper: insightFilter ? "ver todos" : scopeHelper,
        tone: "info",
        iconClassName: "tim-icons icon-chart-bar-32",
        filterValue: null,
        clickable: true,
        active: !insightFilter,
      },
      {
        label: "Ação necessária",
        value: formatCount(arvoreSummary.action),
        helper: insightFilter === "acionavel" ? "filtrando · limpar" : "filtrar",
        tone: "danger",
        iconClassName: "tim-icons icon-alert-circle-exc",
        filterValue: "acionavel",
        clickable: true,
        active: insightFilter === "acionavel",
      },
      {
        label: "Aguardando",
        value: formatCount(arvoreSummary.waiting),
        helper: insightFilter === "dependencia" ? "filtrando · limpar" : "filtrar",
        tone: "warning",
        iconClassName: "tim-icons icon-time-alarm",
        filterValue: "dependencia",
        clickable: true,
        active: insightFilter === "dependencia",
      },
    ];
    // CT-e e Fatura são fila unificada (SFTP + Work): 3º estado "Recebido" como
    // filtro. NÃO mostram "Mais antiga" (a idade misturaria CreationTime do Work
    // com mtime do SFTP).
    cards.push({
      label: "Recebido",
      value: formatCount(arvoreSummary.naoProcessado),
      helper: insightFilter === "naoProcessado" ? "filtrando · limpar" : "no SFTP, não processado",
      tone: "info",
      iconClassName: "tim-icons icon-cloud-download-93",
      filterValue: "naoProcessado",
      clickable: true,
      active: insightFilter === "naoProcessado",
    });
    return cards;
  }, [isNf, displayTotal, scopeSummary, arvoreSummary, verTodos, insightFilter]);

  const clearSelection = () => {
    setSelected(new Set());
    setSelectedRows(new Map());
    setExpanded(new Set());
  };

  const applySearch = () => {
    setSearch(searchDraft.trim());
    clearSelection();
    setPage(1);
  };

  // Busca ativa: filtra sozinha conforme o usuário digita (sem botão Buscar), com
  // um pequeno debounce para não refazer a consulta a cada tecla. Enter ainda
  // aplica na hora. Os filtros avançados (datas) seguem aplicando à parte.
  useEffect(() => {
    if (searchDraft.trim() === search) return undefined;
    const t = setTimeout(() => {
      setSearch(searchDraft.trim());
      setSelected(new Set());
      setSelectedRows(new Map());
      setPage(1);
    }, 450);
    return () => clearTimeout(t);
  }, [searchDraft, search]);

  const clearFilters = () => {
    setSearch("");
    setSearchDraft("");
    setIdTransportadora("");
    setCategoria("all");
    setPendencia(null);
    setDataInicio("");
    setDataFim("");
    setDataBase("emissao");
    setNfHours(DEFAULT_NF_HOURS);
    setNfErrorType("");
    setSelectedKey(null);
    clearSelection();
    setPage(1);
  };

  // Chips dos cards: NF filtra por tipo de erro (nfErrorType), CT-e/Fatura por
  // acionável/dependência (pendencia). Passar null/'' limpa o recorte.
  const applyInsightFilter = (value) => {
    if (isNf) setNfErrorType(value || "");
    else setPendencia(value || null);
    clearSelection();
    setPage(1);
  };

  // Clicar num nó da árvore dirige os filtros da grade (recorte = transportadora
  // [+ categoria]). Transportadora "Sem nome" (id 0) cai em "todas".
  // Volta o recorte da árvore para "todas" (sem transportadora/categoria).
  const limparRecorteLupa = () => {
    setMotivo("");
    setMotivoCte("");
    setEmbarcadorRecorte("");
  };

  const verTodasTransportadoras = () => {
    setSelectedKey(null);
    setIdTransportadora("");
    setCategoria("all");
    setPendencia(null);
    limparRecorteLupa();
    clearSelection();
    setPage(1);
  };

  // PO1-3409: mantém a busca e tira os recortes locais que escondiam o documento.
  const mostrarBuscaSemFiltros = () => {
    verTodasTransportadoras();
    setDataInicio("");
    setDataFim("");
  };

  // PO1-3325: recorte do nó da Lupa na grade (transportadora + categoria +
  // motivo [+ motivo do CT-e, + embarcador na visão todos]; documento = busca
  // pelo número) e rola até a grade.
  const aplicarRecorteLupa = (recorte, rolar = true) => {
    const rec = recorte || {};
    setSelectedKey(Object.keys(rec).length ? "lupa" : null);
    setIdTransportadora(rec.idTransportadora ? String(rec.idTransportadora) : "");
    setCategoria(rec.categoria || "all");
    setMotivo(rec.motivo || "");
    setMotivoCte(rec.motivoCte || "");
    setEmbarcadorRecorte(rec.idEmbarcador ? String(rec.idEmbarcador) : "");
    setPendencia(null);
    const busca = rec.numero ? String(rec.numero) : "";
    setSearch(busca);
    setSearchDraft(busca);
    clearSelection();
    setPage(1);
    if (!rolar) return;
    const alvo = document.getElementById("importacao-documentos-na-fila");
    if (alvo && alvo.scrollIntoView) alvo.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const selectTreeNode = (node) => {
    // Clicar de novo no nó já selecionado volta para "todas" (toggle).
    if (node && node.key === selectedKey) {
      verTodasTransportadoras();
      return;
    }
    const rec = (node && node.recorte) || {};
    setSelectedKey(node ? node.key : null);
    setIdTransportadora(rec.idTransportadora ? String(rec.idTransportadora) : "");
    setCategoria(rec.categoria || "all");
    limparRecorteLupa();
    setPendencia(null);
    clearSelection();
    setPage(1);
  };

  const toggleTreeNode = (key) => {
    setExpandedTree((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const fetchRows = useCallback(() => {
    // CT-e e Fatura derivam a grade no cliente (fetchFila); aqui só NF, paginado
    // no servidor.
    if (isTree) return;
    setLoading(true);
    setFetchError(null);

    const params = { page, pageSize };
    if (empresaId) params.embarcadorId = empresaId;

    if (isNf) {
      params.hours = nfHours;
      if (search) params.search = search;
      if (nfErrorType) params.errorType = nfErrorType;
    } else {
      params.sort = sort;
      params.order = order;
      if (search) params.search = search;
      if (transportadoraEfetiva) params.idTransportadora = transportadoraEfetiva;
      if (categoria && categoria !== "all") params.categoria = categoria;
      if (pendencia) params.pendencia = pendencia;
      if (dataInicio) params.dataInicio = dataInicio;
      if (dataFim) params.dataFim = dataFim;
    }

    api
      .get(`/autopilot/importacao/${tab}`, { params })
      .then((res) => {
        setRows(res.data.rows || []);
        setTotal(res.data.total || 0);
        setResumo(res.data.resumo || null);
        setLoading(false);
      })
      .catch((err) => {
        setFetchError((err.response && err.response.data && err.response.data.error) || "Erro ao carregar a fila");
        setRows([]);
        setTotal(0);
        setResumo(null);
        setLoading(false);
      });
  }, [empresaId, tab, isNf, isTree, page, pageSize, sort, order, search, transportadoraEfetiva, categoria, pendencia, dataInicio, dataFim, nfHours, nfErrorType]);

  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  // Árvore de triagem (CT-e/Fatura): escopo = embarcador + transportadora do
  // header + busca + período. categoria/pendência NÃO entram (a árvore É isso).
  const fetchArvore = useCallback(() => {
    if (isNf) {
      setArvore([]);
      return;
    }
    // CT-e/Fatura: a árvore vem junto com a fila completa (fetchFila), não aqui.
    if (isTree) return;
    setArvoreLoading(true);
    const params = {};
    if (empresaId) params.embarcadorId = empresaId;
    if (headerTransportadoraId) params.idTransportadora = headerTransportadoraId;
    if (search) params.search = search;
    if (dataInicio) params.dataInicio = dataInicio;
    if (dataFim) params.dataFim = dataFim;
    api
      .get(`/autopilot/importacao/${tab}/arvore`, { params })
      .then((res) => {
        setArvore(res.data.arvore || []);
        setArvoreLoading(false);
      })
      .catch(() => {
        setArvore([]);
        setArvoreLoading(false);
      });
  }, [isNf, isTree, tab, empresaId, headerTransportadoraId, search, dataInicio, dataFim]);

  useEffect(() => {
    fetchArvore();
  }, [fetchArvore]);

  // CT-e e Fatura: um único fetch ao vivo traz a fila completa + a árvore do escopo
  // (embarcador + transportadora do header + período). A grade e a árvore derivam
  // disso no cliente, então card/busca/seleção de transportadora não re-listam o
  // SFTP — só trocar embarcador/transportadora-do-header/período (ou atualizar).
  const fetchFila = useCallback(() => {
    if (!isTree) return;
    setLoading(true);
    setArvoreLoading(true);
    setFetchError(null);
    // Escopo do fetch = embarcador + transportadora do header. Data, busca,
    // categoria e tipo de pendência são recorte no cliente (instantâneos).
    const params = {};
    if (empresaId) params.embarcadorId = empresaId;
    if (headerTransportadoraId) params.idTransportadora = headerTransportadoraId;
    // PO1-3055: por padrão o backend lista só canais com vínculo ativo E
    // recepção nos últimos 90 dias. "Ver todos" desliga os dois filtros.
    if (verTodosCanais) params.verTodos = true;
    api
      .get(`/autopilot/importacao/${tab}/fila`, {
        params,
        // PO1-3055: sem teto, o axios espera para sempre (default 0) — com o
        // SFTP fora a tela ficava ~4 min "carregando". Precedente: PO1-2890.
        timeout: TIMEOUT_FILA_MS,
      })
      .then((res) => {
        setFilaCompleta(res.data.rows || []);
        setArvore(res.data.arvore || []);
        // Estado do SFTP: distingue "nada pendente" de "não consegui ler".
        setSftpIndisponivel(!!res.data.sftpIndisponivel);
        setCatalogo(res.data.catalogo || {});
        setCanaisComFalha(res.data.canaisComFalha || []);
        setLoading(false);
        setArvoreLoading(false);
      })
      .catch((err) => {
        setFetchError((err.response && err.response.data && err.response.data.error) || "Erro ao carregar a fila");
        setFilaCompleta([]);
        setArvore([]);
        // O catch zera a árvore, e os cards somam a árvore: sem marcar o erro
        // aqui, "erro" e "fila vazia" viravam o MESMO zero na tela.
        setSftpIndisponivel(true);
        setCanaisComFalha([]);
        setLoading(false);
        setArvoreLoading(false);
      });
  }, [isTree, tab, empresaId, headerTransportadoraId, verTodosCanais]);

  useEffect(() => {
    fetchFila();
  }, [fetchFila]);

  const fetchCounts = useCallback(() => {
    if (isLupa) return; // a Lupa não mostra os cards de fila
    const params = {};
    if (empresaId) params.embarcadorId = empresaId;
    // Mesmo escopo do header que filtra a lista: o filtro por transportadora
    // vale para CT-e e Fatura (NF não tem dimensão de transportadora).
    if (headerTransportadoraId) params.idTransportadora = headerTransportadoraId;
    if (verTodosCanais) params.verTodos = true;
    api
      .get("/autopilot/importacao/contadores", { params, timeout: TIMEOUT_FILA_MS })
      .then((res) => setCounts(res.data || { cte: null, fatura: null, nf: null }))
      .catch(() => setCounts({ cte: null, fatura: null, nf: null }));
  }, [isLupa, empresaId, headerTransportadoraId, verTodosCanais]);

  useEffect(() => {
    fetchCounts();
  }, [fetchCounts]);

  // NF no SFTP: lista a origem (recebidas, não processadas) do embarcador do header.
  const fetchNfSftp = useCallback(() => {
    if (!(tab === "nf" && nfSubview === "sftp")) return;
    setNfSftpLoading(true);
    setNfSftpError(null);
    const params = {};
    if (empresaId) params.embarcadorId = empresaId;
    api
      .get("/autopilot/importacao/nf/sftp", { params })
      .then((res) => {
        setNfSftpRows((res.data && res.data.rows) || []);
        setNfSftpLoading(false);
      })
      .catch((err) => {
        setNfSftpError((err.response && err.response.data && err.response.data.error) || "Erro ao listar a fila do SFTP");
        setNfSftpRows([]);
        setNfSftpLoading(false);
      });
  }, [tab, nfSubview, empresaId]);

  useEffect(() => {
    fetchNfSftp();
  }, [fetchNfSftp]);

  // "Processar fila agora": dispara o job de ingestão de NF (por embarcador).
  const processarNfFila = () => {
    if (!empresaId) {
      setNfSftpError("Selecione um embarcador no topo para processar a fila (o job é por embarcador).");
      return;
    }
    setProcessandoNf(true);
    setNfSftpError(null);
    api
      .post("/autopilot/importacao/nf/sftp/processar", { embarcadorId: empresaId })
      .then(() => {
        setProcessandoNf(false);
        // dá um tempo pro job mover os arquivos e relista a origem.
        setTimeout(fetchNfSftp, 1500);
      })
      .catch((err) => {
        setProcessandoNf(false);
        setNfSftpError((err.response && err.response.data && err.response.data.error) || "Falha ao disparar o processamento");
      });
  };

  // Rejeita uma NF do SFTP: move Origem→Rejeitado e relista.
  const rejeitarNfSftpArquivo = (row) => {
    setNfSftpActionFile(row.nomeArquivo);
    const body = { arquivos: [{ nomeArquivo: row.nomeArquivo, idEmbarcador: row.idEmbarcador }] };
    if (empresaId) body.embarcadorId = empresaId;
    api
      .post("/autopilot/importacao/nf/sftp/rejeitar", body)
      .then(() => {
        setNfSftpActionFile(null);
        fetchNfSftp();
      })
      .catch((err) => {
        setNfSftpActionFile(null);
        setNfSftpError((err.response && err.response.data && err.response.data.error) || "Falha ao rejeitar o arquivo");
      });
  };

  const exportCsv = () => {
    setExporting(true);
    const params = {};
    if (empresaId) params.embarcadorId = empresaId;
    if (search) params.search = search;
    if (isNf) {
      params.hours = nfHours;
      if (nfErrorType) params.errorType = nfErrorType;
    } else {
      if (transportadoraEfetiva) params.idTransportadora = transportadoraEfetiva;
      if (categoria && categoria !== "all") params.categoria = categoria;
      if (pendencia) params.pendencia = pendencia;
      if (dataInicio) params.dataInicio = dataInicio;
      if (dataFim) params.dataFim = dataFim;
    }
    api
      .get(`/autopilot/importacao/${tab}/export`, { params, responseType: "blob" })
      .then((res) => {
        const url = window.URL.createObjectURL(new Blob([res.data], { type: "text/csv;charset=utf-8" }));
        const a = document.createElement("a");
        a.href = url;
        a.download = `importacao-${tab}-${new Date().toISOString().slice(0, 10)}.csv`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
        setExporting(false);
      })
      .catch(() => setExporting(false));
  };

  // Mudança no seletor global do header zera seleção e volta à página 1.
  // O recorte da árvore também volta ao estado inicial (sem nó selecionado).
  useEffect(() => {
    setSelected(new Set());
    setSelectedRows(new Map());
    setExpanded(new Set());
    setSelectedKey(null);
    setIdTransportadora("");
    setCategoria("all");
    setMotivo("");
    setMotivoCte("");
    setEmbarcadorRecorte("");
    setPage(1);
  }, [empresaId, headerTransportadoraId]);

  const switchTab = (next) => {
    if (next === tab) return;
    setTab(next);
    setPage(1);
    setSelected(new Set());
    setSelectedRows(new Map());
    setExpanded(new Set());
    setSelectedKey(null);
    setExpandedTree(new Set());
    setIdTransportadora("");
    setCategoria("all");
    limparRecorteLupa();
    setPendencia(null);
    setNfErrorType("");
    setSearch("");
    setSearchDraft("");
    const params = new URLSearchParams();
    if (next !== "cte") params.set("tab", next);
    const qs = params.toString();
    history.replace({ pathname: location.pathname, search: qs ? `?${qs}` : "" });
  };

  const toggleSort = (col) => {
    if (sort === col) {
      setOrder(order === "asc" ? "desc" : "asc");
    } else {
      setSort(col);
      setOrder("asc");
    }
    setPage(1);
  };

  const sortIndicator = (col) => (sort === col ? (order === "asc" ? " ↑" : " ↓") : "");

  const pageIds = useMemo(() => displayRows.map((r) => r[idKey]), [displayRows, idKey]);
  const allPageSelected = pageIds.length > 0 && pageIds.every((id) => selected.has(id));

  const toggleSelectAll = () => {
    const next = new Set(selected);
    const nextRows = new Map(selectedRows);
    if (allPageSelected) {
      displayRows.forEach((r) => {
        next.delete(r[idKey]);
        nextRows.delete(r[idKey]);
      });
    } else {
      displayRows.forEach((r) => {
        next.add(r[idKey]);
        nextRows.set(r[idKey], r);
      });
    }
    setSelected(next);
    setSelectedRows(nextRows);
  };

  const toggleSelect = (row) => {
    const id = row[idKey];
    const next = new Set(selected);
    const nextRows = new Map(selectedRows);
    if (next.has(id)) {
      next.delete(id);
      nextRows.delete(id);
    } else {
      next.add(id);
      nextRows.set(id, row);
    }
    setSelected(next);
    setSelectedRows(nextRows);
  };

  const toggleExpand = (id) => {
    const next = new Set(expanded);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setExpanded(next);
  };

  // Monta o payload de uma lista de linhas: ids do Work_* + descritores dos
  // arquivos só-no-SFTP ("não processado", sem idWork* → mandamos o arquivo).
  const splitRows = (rowsList) => {
    const ids = [];
    const naoProcessados = [];
    (rowsList || []).forEach((r) => {
      if (!r) return;
      if (r.naoProcessado) {
        naoProcessados.push({
          nomeArquivo: r.NomeArquivo,
          idTransportadora: r.idTransportadora,
          idEmbarcador: r.idEmbarcador,
        });
      } else {
        const workId = r.idWorkCte != null ? r.idWorkCte : r.idWorkFatura;
        if (workId != null) ids.push(workId);
      }
    });
    return { ids, naoProcessados };
  };

  // Atualiza o grid depois de uma ação. Reprocessar (NF) lê o grid do Loki, que tem
  // lag de indexação: um refetch imediato ainda traz o erro antigo. Então remove na
  // hora as chaves OK (otimista) e reconcilia depois. As demais (CT-e/Fatura) refazem
  // a fila do SFTP, que reflete a mudança na hora.
  const posAcao = (acao, data) => {
    if (acao === "reprocessar") {
      const okChaves = new Set(((data && data.results) || []).filter((r) => r && r.ok).map((r) => r.chaveacesso));
      if (okChaves.size) {
        setRows((prev) => prev.filter((r) => !okChaves.has(r.chaveacesso)));
        setTotal((t) => Math.max(0, t - okChaves.size));
      }
      fetchCounts();
      // Loki demora a indexar o evento de sucesso; reconcilia sem re-adicionar cedo.
      setTimeout(() => { fetchRows(); fetchCounts(); }, 8000);
      return;
    }
    fetchRows();
    fetchFila();
    fetchCounts();
  };

  // acaoParam permite disparar a ação direto (sem o modal de confirmação) — usado
  // pelo Integrar, que não é destrutivo. Rejeitar/Reprocessar passam pelo confirm.
  const runAction = (acaoParam) => {
    const acao = acaoParam || (confirmAction && confirmAction.acao);
    if (!acao) return;
    let url;
    const body = {};
    if (empresaId) body.embarcadorId = empresaId;

    // Em lote: as linhas vêm da seleção (selectedRows).
    const splitFila = () => splitRows(Array.from(selected).map((sid) => selectedRows.get(sid)));

    if (acao === "reprocessar") {
      url = "/autopilot/importacao/nf/reprocessar-lote";
      body.chaves = Array.from(selected);
    } else if (acao === "integrar") {
      // CT-e e Fatura têm integração em lote (NF usa reprocessar).
      url = `/autopilot/importacao/${tab}/integrar-lote`;
      if (isTree) {
        const s = splitFila();
        body.ids = s.ids;
        body.naoProcessados = s.naoProcessados;
      } else {
        body.ids = Array.from(selected);
      }
    } else {
      url = `/autopilot/importacao/${tab}/rejeitar-lote`;
      if (isTree) {
        const s = splitFila();
        body.ids = s.ids;
        body.naoProcessados = s.naoProcessados;
      } else {
        body.ids = Array.from(selected);
      }
      if (comentario.trim()) body.comentario = comentario.trim();
    }

    setActionLoading(true);
    api
      .post(url, body)
      .then((res) => {
        setActionLoading(false);
        setConfirmAction(null);
        setComentario("");
        setActionResult({ acao, ...res.data });
        setSelected(new Set());
        setSelectedRows(new Map());
        posAcao(acao, res.data);
      })
      .catch((err) => {
        setActionLoading(false);
        setActionResult({
          acao,
          error: (err.response && err.response.data && err.response.data.error) || "Falha ao executar a ação",
        });
        setConfirmAction(null);
      });
  };

  // Ação por LINHA, direto e sem confirm: Integrar (CT-e/Fatura) ou Reprocessar
  // (NF) um único documento pelo botão da coluna Ações, sem precisar selecionar.
  const runRowAction = (row) => {
    const acao = isNf ? "reprocessar" : "integrar";
    const rid = row[idKey];
    const body = {};
    if (empresaId) body.embarcadorId = empresaId;
    let url;
    if (isNf) {
      url = "/autopilot/importacao/nf/reprocessar-lote";
      body.chaves = [row.chaveacesso];
    } else {
      url = `/autopilot/importacao/${tab}/integrar-lote`;
      const s = splitRows([row]);
      body.ids = s.ids;
      body.naoProcessados = s.naoProcessados;
    }
    setRowActionId(rid);
    api
      .post(url, body)
      .then((res) => {
        setRowActionId(null);
        setActionResult({ acao, ...res.data });
        posAcao(acao, res.data);
      })
      .catch((err) => {
        setRowActionId(null);
        setActionResult({
          acao,
          error: (err.response && err.response.data && err.response.data.error) || "Falha ao executar a ação",
        });
      });
  };

  // Abre o chat do Autopilot já com o contexto do documento da linha (mesma
  // experiência do botão Autopilot no monitor; resposta gerada ao vivo).
  const abrirAutopilot = (row) => {
    let title;
    let linhas;
    if (isNf) {
      const tipoErro =
        row.errortype === "business-error" ? "Negócio" : row.errortype === "technical-error" ? "Técnico" : row.errortype || "-";
      title = `NF-e ${row.numero || ""}`.trim();
      linhas = [
        "Analise esta NF-e com erro de integração:",
        `- Número: ${row.numero || "-"}`,
        `- Chave de acesso: ${row.chaveacesso || "-"}`,
        `- Tipo de erro: ${tipoErro}`,
        `- Erro: "${row.errormessage || "-"}"`,
      ];
    } else {
      const tipoDoc = isCte ? "CTe" : "Fatura";
      title = `${tipoDoc} ${row.Numero || ""}`.trim();
      linhas = [
        `Analise este documento (${tipoDoc}) com pendência de integração:`,
        `- Número: ${row.Numero || "-"}`,
        `- Transportadora: ${row.Fantasia || "-"}`,
        `- Arquivo: ${row.NomeArquivo || "-"}`,
      ];
      if (!isCte && row.Val_TotalFatura) linhas.push(`- Valor: ${formatCurrency(row.Val_TotalFatura)}`);
      if (row.Inconsistencias) linhas.push(`- Inconsistência: "${row.Inconsistencias}"`);
      else if (row.naoProcessado) linhas.push("- Situação: recebido no SFTP, ainda não processado pelo sistema.");
    }
    linhas.push("", "O que causou esse problema e quais são os passos para resolver?");
    setChatModal({ open: true, initialMessage: linhas.join("\n"), title });
  };

  const totalPages = Math.max(1, Math.ceil(displayTotal / pageSize));
  const colSpanTotal = (isNf ? 8 : isCte ? 8 : 9) + (verTodos ? 1 : 0);

  const actionResultTitle =
    actionResult && actionResult.acao === "integrar"
      ? "Resultado da integração"
      : actionResult && actionResult.acao === "reprocessar"
      ? "Resultado do reprocessamento"
      : "Resultado da rejeição";

  // Busca + botão Filtros (reusado: no header da árvore quando isTree, ou no
  // card próprio do NF). Mantém um só lugar de verdade pra esses controles.
  const buscaRow = (
    <div style={{ display: "flex", gap: "0.5rem", alignItems: "center", flexWrap: "wrap", flex: "0 1 480px", minWidth: 260 }}>
      <div style={{ flex: "1 1 220px", minWidth: 200 }}>
        {/* Estilo espelhado da Auditoria de Fatura: lupa interna + alto contraste. */}
        <div style={{ position: "relative" }}>
          <i
            className="tim-icons icon-zoom-split"
            style={{
              position: "absolute",
              left: "0.85rem",
              top: "50%",
              transform: "translateY(-50%)",
              color: "var(--everlog-card-muted)",
              fontSize: "0.85rem",
              pointerEvents: "none",
              zIndex: 1,
            }}
          />
          <Input
            type="text"
            value={searchDraft}
            placeholder={isNf ? "Buscar por chave, número ou mensagem" : "Buscar por arquivo, número ou transportadora"}
            onChange={(e) => setSearchDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                applySearch();
              }
            }}
            style={{
              minHeight: 46,
              paddingLeft: "2.4rem",
              borderRadius: 10,
              border: "1px solid var(--theme-accent-border)",
              background: "var(--everlog-card-surface-field, var(--everlog-card-surface))",
              color: "var(--everlog-card-title)",
              boxShadow: "inset 0 0 0 1px var(--everlog-card-border-faint)",
            }}
          />
        </div>
      </div>
      <Button
        size="sm"
        onClick={() => setMostrarFiltros((v) => !v)}
        aria-expanded={mostrarFiltros}
        title="Filtros adicionais"
        style={{
          minHeight: 46,
          border: "1px solid var(--theme-accent-border)",
          borderRadius: 10,
          background: mostrarFiltros ? "var(--theme-accent-glow, transparent)" : "transparent",
          color: mostrarFiltros ? "var(--theme-accent)" : "var(--everlog-card-text)",
          fontWeight: 600,
        }}
      >
        <i className="tim-icons icon-settings-gear-63" style={{ marginRight: "0.4rem" }} />
        Filtros
        {filtrosAvancadosCount > 0 && (
          <span
            style={{
              marginLeft: "0.4rem",
              background: AUTOPILOT_COLORS.accent,
              color: "#fff",
              borderRadius: 999,
              padding: "0 0.4rem",
              fontSize: "0.7rem",
              fontWeight: 700,
            }}
          >
            {filtrosAvancadosCount}
          </span>
        )}
      </Button>
    </div>
  );

  const filtrosAvancadosBloco = mostrarFiltros ? (
    <div style={{ marginTop: "0.9rem", paddingTop: "0.85rem", borderTop: `1px solid ${AUTOPILOT_COLORS.divider}` }}>
      {/* Layout em <div> (não Row/Col) para funcionar tanto no card estreito
          ao lado da árvore quanto no card do NF. */}
      {isNf ? (
        <>
          <div style={{ marginBottom: "0.7rem" }}>
            <label style={filterLabelStyle}>Janela</label>
            <Input
              type="select"
              bsSize="sm"
              value={nfHours}
              onChange={(e) => {
                setNfHours(Number(e.target.value));
                clearSelection();
                setPage(1);
              }}
            >
              {NF_WINDOWS.map((w) => (
                <option key={w.value} value={w.value}>
                  {w.label}
                </option>
              ))}
            </Input>
          </div>
          <div>
            <label style={filterLabelStyle}>Tipo de erro</label>
            <Input
              type="select"
              bsSize="sm"
              value={nfErrorType}
              onChange={(e) => {
                setNfErrorType(e.target.value);
                clearSelection();
                setPage(1);
              }}
            >
              <option value="">Todos</option>
              <option value="business-error">Negócio</option>
              <option value="technical-error">Técnico</option>
            </Input>
          </div>
        </>
      ) : (
        <div>
          <div style={{ marginBottom: "0.7rem" }}>
            <label style={filterLabelStyle}>Base da data</label>
            <Input
              type="select"
              bsSize="sm"
              value={dataBase}
              onChange={(e) => {
                setDataBase(e.target.value);
                clearSelection();
                setPage(1);
              }}
            >
              <option value="emissao">Emissão</option>
              <option value="vencimento">Vencimento</option>
            </Input>
          </div>
          <label style={filterLabelStyle}>Na fila desde</label>
          <div style={{ display: "flex", gap: "0.4rem", flexWrap: "wrap" }}>
            <Input
              type="date"
              bsSize="sm"
              style={{ flex: "1 1 130px", minWidth: 120 }}
              value={dataInicio}
              onChange={(e) => {
                setDataInicio(e.target.value);
                clearSelection();
                setPage(1);
              }}
            />
            <Input
              type="date"
              bsSize="sm"
              style={{ flex: "1 1 130px", minWidth: 120 }}
              value={dataFim}
              onChange={(e) => {
                setDataFim(e.target.value);
                clearSelection();
                setPage(1);
              }}
            />
          </div>
        </div>
      )}
      <div style={{ display: "flex", justifyContent: "flex-end", marginTop: "0.7rem" }}>
        <Button size="sm" outline onClick={clearFilters} disabled={!canClearFilters || loading}>
          Limpar filtros
        </Button>
      </div>
    </div>
  ) : null;

  // Toggle da aba NF-e: erros (Loki) x fila do SFTP (recebidas, não processadas).
  const nfToggle = isNf ? (
    <div
      style={{
        display: "inline-flex",
        border: `1px solid ${AUTOPILOT_COLORS.border}`,
        borderRadius: 10,
        overflow: "hidden",
        marginBottom: "0.9rem",
      }}
    >
      {[
        { key: "erros", label: "Erros (Loki)" },
        { key: "sftp", label: "Na fila do SFTP" },
      ].map((opt) => {
        const active = nfSubview === opt.key;
        return (
          <button
            key={opt.key}
            type="button"
            onClick={() => setNfSubview(opt.key)}
            aria-pressed={active}
            style={{
              border: "none",
              background: active ? "var(--theme-accent-glow, " + AUTOPILOT_COLORS.insetSurfaceActive + ")" : "transparent",
              color: active ? "var(--theme-accent)" : AUTOPILOT_COLORS.text,
              fontWeight: active ? 700 : 500,
              fontSize: "0.8rem",
              padding: "0.5rem 0.95rem",
              cursor: "pointer",
            }}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  ) : null;

  // Tabela plana da fila de NF no SFTP (sem transportadora/árvore).
  const nfSftpBloco = (
    <>
      {nfSftpError && (
        <div
          style={{
            padding: "0.6rem 0.9rem",
            marginBottom: "0.7rem",
            borderRadius: 6,
            background: AUTOPILOT_COLORS.statusDangerBg,
            border: `1px solid ${AUTOPILOT_COLORS.statusDangerBorder}`,
            color: AUTOPILOT_COLORS.statusDanger,
            fontSize: "0.82rem",
          }}
        >
          {nfSftpError}
        </div>
      )}
      {nfSftpLoading ? (
        <div style={{ textAlign: "center", padding: "2.5rem 0" }}>
          <ClipLoader size={28} color="var(--theme-accent)" />
        </div>
      ) : (
        <Table responsive style={{ marginBottom: "0.6rem" }}>
          <thead>
            <tr>
              <th style={thStyle}>Arquivo</th>
              {verTodos && <th style={thStyle}>Embarcador</th>}
              <th style={thStyle}>Recebido em</th>
              <th style={thStyle}>Tamanho</th>
              <th style={thStyle}>Ações</th>
            </tr>
          </thead>
          <tbody>
            {nfSftpRows.length === 0 && (
              <tr>
                <td colSpan={verTodos ? 5 : 4} style={{ ...tdStyle, textAlign: "center", padding: "1.6rem 0", color: AUTOPILOT_COLORS.muted }}>
                  Nenhuma NF na origem do SFTP{verTodos ? "" : " para este embarcador"}.
                </td>
              </tr>
            )}
            {nfSftpRows.map((row) => (
              <tr key={(row.idEmbarcador || "") + "|" + row.nomeArquivo}>
                <td style={{ ...tdStyle, maxWidth: 320, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={row.nomeArquivo}>
                  {row.nomeArquivo}
                </td>
                {verTodos && <td style={tdStyle}>{row.embarcador || row.idEmbarcador || "-"}</td>}
                <td style={{ ...tdStyle, whiteSpace: "nowrap" }}>{formatDateTime(row.mtime)}</td>
                <td style={{ ...tdStyle, whiteSpace: "nowrap" }}>{formatBytes(row.tamanho)}</td>
                <td style={{ ...tdStyle, whiteSpace: "nowrap" }}>
                  <div style={{ display: "inline-flex", gap: "0.4rem" }}>
                    <button
                      type="button"
                      onClick={() => setViewer({ tipo: "nf-sftp", nomeArquivo: row.nomeArquivo, idEmbarcador: row.idEmbarcador })}
                      style={smallActionButtonStyle}
                    >
                      Ver arquivo
                    </button>
                    <button
                      type="button"
                      onClick={() => rejeitarNfSftpArquivo(row)}
                      disabled={nfSftpActionFile === row.nomeArquivo}
                      style={smallRejeitarButtonStyle}
                    >
                      {nfSftpActionFile === row.nomeArquivo ? "Rejeitando..." : "Rejeitar"}
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
    </>
  );

  const cardLupa = (
    <Card style={sectionCardStyle}>
      <CardHeader style={autopilotHeaderStyle}>
        <div style={{ ...panelHeaderStyle, flexWrap: "wrap" }}>
          <div>
            <div style={panelTitleStyle}>Fila de integração</div>
            <div style={panelDescriptionStyle}>
              {verTodos
                ? "Todos os embarcadores: o primeiro nível é o embarcador. Sem conferência do arquivo no SFTP; selecione um embarcador para contar só o que tem arquivo."
                : "Embarcador selecionado no cabeçalho. Conta os arquivos que estão na origem do SFTP."}
              {sftpIndisponivel ? " O SFTP não respondeu: arquivos ainda não processados podem estar faltando." : ""}
            </div>
          </div>
          <div
            role="group"
            aria-label="Tipo de documento"
            style={{ display: "inline-flex", border: `1px solid ${AUTOPILOT_COLORS.border}`, borderRadius: 10, overflow: "hidden", flexShrink: 0 }}
          >
            {TAB_OPTIONS.filter((opt) => opt.key !== "nf").map((opt) => {
              const active = tab === opt.key;
              return (
                <button
                  key={opt.key}
                  type="button"
                  onClick={() => switchTab(opt.key)}
                  aria-pressed={active}
                  style={{
                    border: "none",
                    background: active ? "var(--theme-accent-glow, " + AUTOPILOT_COLORS.insetSurfaceActive + ")" : "transparent",
                    color: active ? "var(--theme-accent)" : AUTOPILOT_COLORS.text,
                    fontWeight: active ? 700 : 500,
                    fontSize: "0.78rem",
                    padding: "0.4rem 0.8rem",
                    cursor: "pointer",
                  }}
                >
                  {opt.label}
                </button>
              );
            })}
          </div>
        </div>
      </CardHeader>
      <CardBody style={autopilotBodyStyle}>
        {fetchError ? (
          <div style={{ color: AUTOPILOT_COLORS.statusDanger, fontSize: "0.85rem", padding: "1rem 0" }}>{fetchError}</div>
        ) : (
          <LupaInconsistencias
            key={tab}
            rows={filaCompleta}
            catalogo={catalogo}
            tipo={tab}
            verTodos={verTodos}
            loading={arvoreLoading}
            onFoco={(rec) => aplicarRecorteLupa(rec, false)}
            onRevisar={aplicarRecorteLupa}
          />
        )}
      </CardBody>
    </Card>
  );

  return (
    <div className="content">
      <Card style={{ ...autopilotCardStyle, background: "var(--everlog-card-hero-bg)" }}>
        <CardHeader style={heroHeaderStyle}>
          <Row className="align-items-start">
            <Col>
              <div style={autopilotEyebrowStyle}>Sustentação</div>
              <h2 style={heroTitleStyle}>{isLupa ? "Lupa de Inconsistências" : "Integração de Documentos"}</h2>
              <p style={heroDescriptionStyle}>
                {isLupa
                  ? "Onde a fila de integração está travada, em bolhas: cada bolha é um motivo; clique para descer até a transportadora e o documento. A cor diz quem resolve, e os documentos do recorte aparecem logo abaixo, com as ações."
                  : "Fila de documentos recebidos aguardando entrada no sistema, por transportadora e tipo."}
              </p>
            </Col>
            <Col xs="auto">
              <div style={heroActionsStyle}>
                <Button
                  className="btn-sm"
                  style={autopilotSecondaryButtonStyle}
                  disabled={loading}
                  onClick={() => {
                    fetchRows();
                    fetchFila();
                    fetchCounts();
                  }}
                >
                  Atualizar
                </Button>
                <Button
                  className="btn-sm"
                  color="success"
                  outline
                  disabled={exporting || loading || displayTotal === 0}
                  onClick={exportCsv}
                >
                  {exporting ? "Exportando..." : "Exportar CSV"}
                </Button>
              </div>
            </Col>
          </Row>
        </CardHeader>
      </Card>

      {isLupa && cardLupa}

      {!isLupa && (
      <Card style={sectionCardStyle}>
        <CardHeader style={autopilotHeaderStyle}>
          <div style={panelTitleStyle}>Filas de integração</div>
          <div style={panelDescriptionStyle}>
            Escolha o tipo de documento e veja os sinais principais do recorte atual antes de abrir a grade.
          </div>
        </CardHeader>
        <CardBody style={autopilotBodyStyle}>
          <div style={queueTabsGridStyle}>
            {TAB_OPTIONS.map((option) => (
              <QueueTabCard
                key={option.key}
                option={option}
                active={tab === option.key}
                count={counts[option.key]}
                onClick={() => switchTab(option.key)}
              />
            ))}
          </div>

          {/* PO1-3055: o aviso fica ACIMA dos cards de propósito. O banner de
              erro que já existia estava lá embaixo, depois da grade — o
              operador via "0" no topo e nunca rolava até o aviso.
              Extraído para componente de módulo: inline, os dois blocos
              condicionais estouravam a análise de fluxo do react-hooks neste
              componente (~1.150 linhas) e o build quebrava com falso positivo
              de "hook chamado condicionalmente". */}
          {isTree && (
            <AvisoLeituraSftp
              sftpIndisponivel={sftpIndisponivel}
              canaisComFalha={canaisComFalha}
            />
          )}
          {isTree && !sftpIndisponivel && canaisComFalha.length > 0 && (
            <div
              style={{
                padding: "0.6rem 0.9rem",
                marginBottom: "0.7rem",
                borderRadius: 6,
                background: "var(--everlog-status-warning-bg, rgba(255,178,54,0.12))",
                border: "1px solid var(--everlog-status-warning-border, rgba(255,178,54,0.35))",
                fontSize: "0.82rem",
              }}
            >
              {canaisComFalha.length === 1
                ? "1 canal não respondeu nesta leitura"
                : `${canaisComFalha.length} canais não responderam nesta leitura`}
              {" "}({canaisComFalha
                .slice(0, 3)
                .map((c) => c.fantasia || c.canalId)
                .join(", ")}
              {canaisComFalha.length > 3 ? "…" : ""}). Os números abaixo podem estar incompletos.
            </div>
          )}

          {/* Cards de sinais (Loki) não se aplicam à fila do SFTP de NF. */}
          {!nfSftpMode && (
            <div style={insightGridStyle}>
              {queueInsights.map((item) => (
                <InsightTile
                  key={item.label}
                  /* Em CT-e/Fatura os números vêm da árvore (listagem SFTP ao vivo),
                     então o "carregando" do card segue arvoreLoading, não a grade. */
                  loading={isTree ? arvoreLoading : loading}
                  {...item}
                  onClick={item.clickable ? () => applyInsightFilter(item.active ? null : item.filterValue) : undefined}
                />
              ))}
            </div>
          )}

          {isTree && (
            <BarraVerTodosCanais
              verTodosCanais={verTodosCanais}
              onToggle={() => setVerTodosCanais((v) => !v)}
            />
          )}
        </CardBody>
      </Card>

      )}

      {/* Modo árvore: Triagem (esquerda, cresce) + card de Filtros (direita), lado a lado.
          alignItems:stretch -> os dois cards ficam com a MESMA altura (a do mais alto). */}
      {isTree && !isLupa && (
        <div style={{ display: "flex", gap: "1rem", alignItems: "stretch", flexWrap: "wrap", marginTop: "1rem" }}>
          <Card style={{ ...sectionCardStyle, marginTop: 0, flex: "1 1 560px", minWidth: 340 }}>
            <CardHeader style={autopilotHeaderStyle}>
              <div style={panelHeaderStyle}>
                <div>
                  <div style={panelTitleStyle}>Triagem por transportadora</div>
                  <div style={panelDescriptionStyle}>
                    Expanda uma transportadora para ver os motivos. Clique numa linha para abrir os documentos abaixo.
                  </div>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: "0.75rem", flexShrink: 0 }}>
                {selectedKey && (
                  <button
                    type="button"
                    onClick={verTodasTransportadoras}
                    title="Voltar a mostrar todas as transportadoras"
                    style={{
                      flexShrink: 0,
                      border: "none",
                      background: "transparent",
                      color: "var(--theme-accent)",
                      fontWeight: 600,
                      fontSize: "0.8rem",
                      cursor: "pointer",
                      padding: 0,
                      whiteSpace: "nowrap",
                      display: "inline-flex",
                      alignItems: "center",
                    }}
                  >
                    <i className="tim-icons icon-double-left" style={{ marginRight: "0.35rem" }} />
                    Ver todas
                  </button>
                )}
                </div>
              </div>
            </CardHeader>
            <CardBody style={autopilotBodyStyle}>
              <ImportacaoArvore
                arvore={arvore}
                loading={arvoreLoading}
                expanded={expandedTree}
                onToggle={toggleTreeNode}
                selectedKey={selectedKey}
                onSelect={selectTreeNode}
              />
            </CardBody>
          </Card>

          <Card style={{ ...sectionCardStyle, marginTop: 0, flex: "0 1 320px", minWidth: 280 }}>
            <CardHeader style={autopilotHeaderStyle}>
              <div style={panelHeaderStyle}>
                <div>
                  <div style={panelTitleStyle}>Filtros</div>
                  <div style={panelDescriptionStyle}>Refine a fila por busca e período.</div>
                </div>
              </div>
            </CardHeader>
            <CardBody style={autopilotBodyStyle}>
              {buscaRow}
              {filtrosAvancadosBloco}
            </CardBody>
          </Card>
        </div>
      )}

      {/* NF não tem árvore: card próprio (largura cheia) com toggle Erros x Fila SFTP. */}
      {isNf && (
        <Card style={sectionCardStyle}>
          <CardBody style={autopilotBodyStyle}>
            {nfToggle}
            {nfSftpMode ? (
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "0.6rem" }}>
                <div style={panelDescriptionStyle}>
                  NF recebidas no SFTP que ainda não foram processadas pelo job.
                  {empresaId ? "" : " Selecione um embarcador para poder processar."}
                </div>
                <div style={{ display: "flex", gap: "0.5rem" }}>
                  <Button className="btn-sm" style={autopilotSecondaryButtonStyle} disabled={nfSftpLoading} onClick={fetchNfSftp}>
                    Atualizar
                  </Button>
                  <Button size="sm" color="success" disabled={processandoNf || !empresaId} onClick={processarNfFila}>
                    {processandoNf ? "Processando..." : "Processar fila agora"}
                  </Button>
                </div>
              </div>
            ) : (
              <>
                {buscaRow}
                {filtrosAvancadosBloco}
              </>
            )}
          </CardBody>
        </Card>
      )}

      <Card style={sectionCardStyle} id="importacao-documentos-na-fila">
        <CardHeader style={autopilotHeaderStyle}>
          <div style={tableHeaderStyle}>
            <div>
              <div style={panelTitleStyle}>{nfSftpMode ? "NF no SFTP — recebidas, não processadas" : "Documentos na fila"}</div>
              <div style={panelDescriptionStyle}>
                {nfSftpMode
                  ? nfSftpLoading
                    ? "Carregando..."
                    : `${formatCount(nfSftpRows.length)} arquivo${nfSftpRows.length === 1 ? "" : "s"} na origem${verTodos ? " (todos os embarcadores)" : ""}`
                  : loading
                  ? "Carregando..."
                  : `${formatCount(displayTotal)} pendência${displayTotal === 1 ? "" : "s"}${verTodos ? " em todos os embarcadores (sem conferência do arquivo no SFTP)" : ""}${
                      isTree && selectedKey ? " no recorte selecionado" : ""
                    }`}
                {isNf && !nfSftpMode && !loading && <span> · janela limitada pela retenção dos logs</span>}
              </div>
            </div>
          </div>
        </CardHeader>
        <CardBody style={autopilotBodyStyle}>
          {!nfSftpMode && selected.size > 0 && (
            <div style={selectionToolbarStyle}>
              <div>
                <div style={{ color: AUTOPILOT_COLORS.title, fontWeight: 700, fontSize: "0.88rem" }}>
                  {selectedLabel}
                </div>
                <div style={{ color: AUTOPILOT_COLORS.muted, fontSize: "0.76rem", marginTop: "0.15rem" }}>
                  Ações em lote para a fila {currentTab.label}.
                </div>
              </div>
              <div style={{ display: "flex", flexWrap: "wrap", justifyContent: "flex-end", gap: "0.5rem" }}>
                {!isNf && (
                  <Button
                    size="sm"
                    color="info"
                    disabled={actionLoading}
                    onClick={() => runAction("integrar")}
                  >
                    {actionLoading ? "Integrando..." : `Integrar (${selected.size})`}
                  </Button>
                )}
                {isNf ? (
                  <Button
                    size="sm"
                    color="info"
                    disabled={actionLoading}
                    onClick={() => setConfirmAction({ acao: "reprocessar" })}
                  >
                    Reprocessar ({selected.size})
                  </Button>
                ) : (
                  <Button
                    size="sm"
                    color="danger"
                    outline
                    disabled={actionLoading}
                    onClick={() => setConfirmAction({ acao: "rejeitar" })}
                  >
                    Rejeitar ({selected.size})
                  </Button>
                )}
                <Button size="sm" outline disabled={actionLoading} onClick={clearSelection}>
                  Limpar seleção
                </Button>
              </div>
            </div>
          )}

          {!nfSftpMode && fetchError && (
            <div
              style={{
                padding: "0.6rem 0.9rem",
                marginBottom: "0.7rem",
                borderRadius: 6,
                background: AUTOPILOT_COLORS.statusDangerBg,
                border: `1px solid ${AUTOPILOT_COLORS.statusDangerBorder}`,
                color: AUTOPILOT_COLORS.statusDanger,
                fontSize: "0.82rem",
              }}
            >
              {fetchError}
            </div>
          )}

          {nfSftpMode ? (
            nfSftpBloco
          ) : loading ? (
            <div style={{ textAlign: "center", padding: "2.5rem 0" }}>
              <ClipLoader size={28} color="var(--theme-accent)" />
            </div>
          ) : (
            <Table responsive style={{ marginBottom: "0.6rem" }}>
              <thead>
                <tr>
                  <th style={{ ...thStyle, width: 34 }}>
                    <input type="checkbox" checked={allPageSelected} onChange={toggleSelectAll} />
                  </th>
                  <th style={{ ...thStyle, width: 42 }}>Detalhe</th>
                  {isNf ? (
                    <>
                      <th style={thStyle}>Data</th>
                      <th style={thStyle}>Número</th>
                      <th style={thStyle}>Chave de acesso</th>
                      {verTodos && <th style={thStyle}>Embarcador</th>}
                      <th style={thStyle}>Erro</th>
                      <th style={thStyle}>Tipo</th>
                      <th style={thStyle}>Ações</th>
                    </>
                  ) : (
                    <>
                      <th style={{ ...thStyle, cursor: "pointer" }} onClick={() => toggleSort("numero")}>
                        Número{sortIndicator("numero")}
                      </th>
                      <th style={{ ...thStyle, cursor: "pointer" }} onClick={() => toggleSort("transportadora")}>
                        Transportadora{sortIndicator("transportadora")}
                      </th>
                      {verTodos && (
                        <th style={{ ...thStyle, cursor: "pointer" }} onClick={() => toggleSort("embarcador")}>
                          Embarcador{sortIndicator("embarcador")}
                        </th>
                      )}
                      <th style={thStyle}>Arquivo</th>
                      <th style={{ ...thStyle, cursor: "pointer" }} onClick={() => toggleSort("dataEmissao")}>
                        Emissão{sortIndicator("dataEmissao")}
                      </th>
                      {!isCte && <th style={thStyle}>Valor</th>}
                      {/* Sem "Na fila" em CT-e/Fatura: a data misturaria o
                          CreationTime do Work com o mtime do SFTP (engana). */}
                      <th style={thStyle}>Pendência</th>
                      <th style={thStyle}>Ações</th>
                    </>
                  )}
                </tr>
              </thead>
              <tbody>
                {displayRows.length === 0 && (
                  <tr>
                    <td colSpan={colSpanTotal} style={{ ...tdStyle, textAlign: "center", padding: "1.6rem 0", color: AUTOPILOT_COLORS.muted }}>
                      <VazioGradeFila
                        isTree={isTree}
                        tipo={isCte ? "cte" : "fatura"}
                        search={search}
                        ocultosPelosFiltros={ocultosPelosFiltros}
                        onMostrarSemFiltros={mostrarBuscaSemFiltros}
                        contexto={{ embarcadorId: empresaId || null, transportadoraId: headerTransportadoraId || null }}
                      />
                    </td>
                  </tr>
                )}
                {displayRows.map((row) => {
                  const id = row[idKey];
                  const isExpanded = expanded.has(id);
                  return (
                    <React.Fragment key={id}>
                      <tr>
                        <td style={tdStyle} onClick={(e) => e.stopPropagation()}>
                          <input type="checkbox" checked={selected.has(id)} onChange={() => toggleSelect(row)} />
                        </td>
                        <td style={tdStyle}>
                          <button
                            type="button"
                            style={expandButtonStyle}
                            onClick={() => toggleExpand(id)}
                            aria-label={isExpanded ? "Ocultar detalhe" : "Ver detalhe"}
                          >
                            <i className={isExpanded ? "tim-icons icon-minimal-up" : "tim-icons icon-minimal-down"} />
                          </button>
                        </td>
                        {isNf ? (
                          <>
                            <td style={{ ...tdStyle, whiteSpace: "nowrap" }}>{formatDateTime(row.ts)}</td>
                            <td style={tdStyle}>
                              {row.numero || "-"}
                              {row.serie ? <span style={{ color: AUTOPILOT_COLORS.muted }}> / {row.serie}</span> : null}
                            </td>
                            <td style={{ ...tdStyle, fontFamily: "monospace", fontSize: "0.72rem", maxWidth: 220, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={row.chaveacesso}>
                              {row.chaveacesso}
                            </td>
                            {verTodos && <td style={tdStyle}>{row.razaoSocial || row.idEmbarcador || "-"}</td>}
                            <td style={{ ...tdStyle, maxWidth: 300, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={row.errormessage}>
                              {row.errormessage || "-"}
                            </td>
                            <td style={tdStyle}>
                              <ErrorTypeBadge tipo={row.errortype} />
                            </td>
                            <td style={{ ...tdStyle, whiteSpace: "nowrap" }} onClick={(e) => e.stopPropagation()}>
                              <div style={{ display: "inline-flex", gap: "0.4rem" }}>
                                <button
                                  type="button"
                                  onClick={() => runRowAction(row)}
                                  disabled={rowActionId === id}
                                  style={smallIntegrarButtonStyle}
                                >
                                  {rowActionId === id ? "Reprocessando..." : "Reprocessar"}
                                </button>
                                <button
                                  type="button"
                                  onClick={() => abrirAutopilot(row)}
                                  style={smallAutopilotButtonStyle}
                                  title="Analisar com Autopilot"
                                >
                                  <img src={autopilotMiniIcon} alt="" style={{ width: 12, height: 12, objectFit: "contain" }} />
                                  Autopilot
                                </button>
                                <button
                                  type="button"
                                  onClick={() => setViewer({ tipo: "nf", chaveacesso: row.chaveacesso, numero: row.numero, idEmbarcador: row.idEmbarcador })}
                                  style={smallActionButtonStyle}
                                >
                                  Ver payload
                                </button>
                              </div>
                            </td>
                          </>
                        ) : (
                          <>
                            <td style={{ ...tdStyle, whiteSpace: "nowrap" }}>
                              {row.Numero || "-"}
                              {isCte && row.Serie ? <span style={{ color: AUTOPILOT_COLORS.muted }}> / {row.Serie}</span> : null}
                            </td>
                            <td style={tdStyle}>{row.Fantasia || "-"}</td>
                            {verTodos && <td style={tdStyle}>{row.Embarcador || row.idEmbarcador || "-"}</td>}
                            <td style={{ ...tdStyle, maxWidth: 240, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={row.NomeArquivo}>
                              {row.NomeArquivo}
                            </td>
                            <td style={{ ...tdStyle, whiteSpace: "nowrap" }}>{formatDate(row.Dat_Emissao)}</td>
                            {!isCte && <td style={{ ...tdStyle, whiteSpace: "nowrap" }}>{formatCurrency(row.Val_TotalFatura)}</td>}
                            <td style={tdStyle}>
                              <PendenciaBadge tipo={row.tipoPendencia} />
                            </td>
                            <td style={{ ...tdStyle, whiteSpace: "nowrap" }} onClick={(e) => e.stopPropagation()}>
                              <div style={{ display: "inline-flex", gap: "0.4rem" }}>
                                <button
                                  type="button"
                                  onClick={() => runRowAction(row)}
                                  disabled={rowActionId === id}
                                  style={smallIntegrarButtonStyle}
                                >
                                  {rowActionId === id ? "Integrando..." : "Integrar"}
                                </button>
                                <button
                                  type="button"
                                  onClick={() => abrirAutopilot(row)}
                                  style={smallAutopilotButtonStyle}
                                  title="Analisar com Autopilot"
                                >
                                  <img src={autopilotMiniIcon} alt="" style={{ width: 12, height: 12, objectFit: "contain" }} />
                                  Autopilot
                                </button>
                                <button
                                  type="button"
                                  onClick={() => setViewer({ tipo: tab, nomeArquivo: row.NomeArquivo, idTransportadora: row.idTransportadora, idEmbarcador: row.idEmbarcador })}
                                  style={smallActionButtonStyle}
                                >
                                  Ver arquivo
                                </button>
                              </div>
                            </td>
                          </>
                        )}
                      </tr>
                      {isExpanded && (
                        <tr>
                          <td colSpan={colSpanTotal} style={{ ...tdStyle, background: AUTOPILOT_COLORS.insetSurface, fontSize: "0.78rem" }}>
                            <div style={{ padding: "0.35rem 0.4rem", whiteSpace: "pre-wrap", color: AUTOPILOT_COLORS.textSoft }}>
                              {isNf ? row.errormessage || "Sem detalhe do erro." : row.Inconsistencias || "Sem detalhe de inconsistência."}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </Table>
          )}

          {/* Paginação só na grade normal; a fila do SFTP de NF é lista simples. */}
          {!nfSftpMode && (
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", fontSize: "0.8rem", color: AUTOPILOT_COLORS.muted }}>
              <span>Por página:</span>
              <Input
                type="select"
                bsSize="sm"
                style={{ width: 76 }}
                value={pageSize}
                onChange={(e) => {
                  setPageSize(Number(e.target.value));
                  setPage(1);
                }}
              >
                {PAGE_SIZES.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </Input>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: "0.6rem", fontSize: "0.8rem", color: AUTOPILOT_COLORS.muted }}>
              <Button size="sm" outline disabled={page <= 1 || loading} onClick={() => setPage(1)}>
                Primeira
              </Button>
              <Button size="sm" outline disabled={page <= 1 || loading} onClick={() => setPage(page - 1)}>
                Anterior
              </Button>
              <span>
                Página {page} de {totalPages}
              </span>
              <Button size="sm" outline disabled={page >= totalPages || loading} onClick={() => setPage(page + 1)}>
                Próxima
              </Button>
              <Button size="sm" outline disabled={page >= totalPages || loading} onClick={() => setPage(totalPages)}>
                Última
              </Button>
            </div>
          </div>
          )}
        </CardBody>
      </Card>

      <XmlViewerModal
        isOpen={!!viewer && viewer.tipo === "cte"}
        onClose={() => setViewer(null)}
        nomeArquivo={viewer && viewer.nomeArquivo}
        idTransportadora={viewer && viewer.idTransportadora}
        embarcadorId={(viewer && viewer.idEmbarcador) || empresaId}
      />
      {/* NF no SFTP: mesmo viewer de XML, mas lendo do endpoint da fila de NF. */}
      <XmlViewerModal
        isOpen={!!viewer && viewer.tipo === "nf-sftp"}
        onClose={() => setViewer(null)}
        nomeArquivo={viewer && viewer.nomeArquivo}
        fetchUrl="/autopilot/importacao/nf/sftp/xml"
        fetchParams={
          viewer && viewer.tipo === "nf-sftp"
            ? { nomeArquivo: viewer.nomeArquivo, embarcadorId: viewer.idEmbarcador || empresaId }
            : null
        }
      />
      <TxtViewerModal
        isOpen={!!viewer && viewer.tipo === "fatura"}
        onClose={() => setViewer(null)}
        nomeArquivo={viewer && viewer.nomeArquivo}
        idTransportadora={viewer && viewer.idTransportadora}
        embarcadorId={(viewer && viewer.idEmbarcador) || empresaId}
      />
      <PayloadViewerModal
        isOpen={!!viewer && viewer.tipo === "nf"}
        onClose={() => setViewer(null)}
        chaveacesso={viewer && viewer.chaveacesso}
        numero={viewer && viewer.numero}
        embarcadorId={(viewer && viewer.idEmbarcador) || empresaId}
      />
      <ChatModal
        isOpen={chatModal.open}
        onClose={() => setChatModal({ open: false, initialMessage: null, title: null })}
        initialMessage={chatModal.initialMessage}
        title={chatModal.title}
        empresaId={empresaId}
      />

      <Modal isOpen={!!confirmAction} toggle={() => setConfirmAction(null)} modalClassName="modal-align-top" fade={false}>
        <ModalHeaderBar
          title={
            confirmAction && confirmAction.acao === "integrar"
              ? "Integrar selecionados"
              : confirmAction && confirmAction.acao === "reprocessar"
              ? "Reprocessar selecionadas"
              : "Rejeitar selecionados"
          }
          onClose={() => setConfirmAction(null)}
        />
        <ModalBody>
          {confirmAction && confirmAction.acao === "integrar" && (
            <p style={{ fontSize: "0.85rem", color: AUTOPILOT_COLORS.text }}>
              Os <strong>{selected.size}</strong> arquivos selecionados serão reenviados ao pipeline de integração.
              Se a causa da pendência já foi corrigida, eles entram no sistema; caso contrário, voltam para a fila
              com a inconsistência atualizada.
            </p>
          )}
          {confirmAction && confirmAction.acao === "reprocessar" && (
            <p style={{ fontSize: "0.85rem", color: AUTOPILOT_COLORS.text }}>
              As <strong>{selected.size}</strong> notas selecionadas serão reenviadas para o mesmo endpoint de
              integração, com o payload original. O processamento é sequencial e pode levar alguns segundos por nota.
            </p>
          )}
          {confirmAction && confirmAction.acao === "rejeitar" && (
            <>
              <p style={{ fontSize: "0.85rem", color: AUTOPILOT_COLORS.text }}>
                Os <strong>{selected.size}</strong> itens selecionados serão <strong>rejeitados</strong>: o arquivo
                sai da fila, vai para a pasta Rejeitado do SFTP e o transportador recebe o retorno com os motivos.
                {isCte && " Linhas do mesmo arquivo XML são rejeitadas juntas."}
              </p>
              <label style={filterLabelStyle}>Comentário (vai no retorno ao transportador, opcional)</label>
              <Input
                type="textarea"
                rows={3}
                value={comentario}
                onChange={(e) => setComentario(e.target.value)}
                placeholder="Ex.: arquivo enviado em duplicidade"
              />
            </>
          )}
          <div style={{ display: "flex", justifyContent: "flex-end", gap: "0.5rem", marginTop: "1rem" }}>
            <Button size="sm" outline onClick={() => setConfirmAction(null)} disabled={actionLoading}>
              Cancelar
            </Button>
            <Button
              size="sm"
              color={confirmAction && confirmAction.acao === "rejeitar" ? "danger" : "info"}
              onClick={() => runAction()}
              disabled={actionLoading}
            >
              {actionLoading ? "Executando..." : "Confirmar"}
            </Button>
          </div>
        </ModalBody>
      </Modal>

      <Modal isOpen={!!actionResult} toggle={() => setActionResult(null)} modalClassName="modal-align-top" fade={false} size="lg">
        <ModalHeaderBar title={actionResultTitle} onClose={() => setActionResult(null)} />
        <ModalBody>
          {actionResult && actionResult.error ? (
            <div style={{ color: AUTOPILOT_COLORS.statusDanger, fontSize: "0.85rem" }}>{actionResult.error}</div>
          ) : actionResult ? (
            <>
              <p style={{ fontSize: "0.85rem", color: AUTOPILOT_COLORS.text }}>
                <strong>{actionResult.ok}</strong> de <strong>{actionResult.total}</strong> com sucesso
                {actionResult.fail > 0 && (
                  <span style={{ color: AUTOPILOT_COLORS.statusDanger }}> · {actionResult.fail} com falha</span>
                )}
                {actionResult.acao === "integrar" &&
                  (actionResult.sincrono
                    ? ". Integração executada agora; o resultado abaixo é o real."
                    : ". A fila pode levar alguns minutos para refletir a integração.")}
                {actionResult.acao === "reprocessar" &&
                  ". A fila reflete o resultado na próxima consulta aos logs."}
              </p>
              {Array.isArray(actionResult.results) && actionResult.results.length > 0 && (
                // Layout em <div> (grid), NÃO usa <table>: o CSS do template pinta
                // tabelas dentro de modal com cabeçalho azul e linha branca, o que
                // sumia com o texto claro. Com divs nada disso se aplica.
                <div style={{ marginTop: "0.6rem", border: `1px solid ${AUTOPILOT_COLORS.divider}`, borderRadius: 8, overflow: "hidden" }}>
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "minmax(0, 2fr) 72px minmax(0, 2fr)",
                      gap: "0.6rem",
                      padding: "0.45rem 0.7rem",
                      background: AUTOPILOT_COLORS.insetSurface,
                      color: AUTOPILOT_COLORS.muted,
                      fontSize: "0.68rem",
                      fontWeight: 700,
                      letterSpacing: "0.06em",
                      textTransform: "uppercase",
                    }}
                  >
                    <div>Documento</div>
                    <div>Status</div>
                    <div>Motivo</div>
                  </div>
                  {actionResult.results.map((r, i) => (
                    <div
                      key={i}
                      style={{
                        display: "grid",
                        gridTemplateColumns: "minmax(0, 2fr) 72px minmax(0, 2fr)",
                        gap: "0.6rem",
                        padding: "0.45rem 0.7rem",
                        borderTop: `1px solid ${AUTOPILOT_COLORS.divider}`,
                        background: "transparent",
                        color: AUTOPILOT_COLORS.text,
                        fontSize: "0.82rem",
                        alignItems: "center",
                      }}
                    >
                      <div style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={r.nomeArquivo || r.chaveacesso || String(r.id || "")}>
                        {r.nomeArquivo || r.chaveacesso || r.id || "-"}
                      </div>
                      <div style={{ color: r.ok ? AUTOPILOT_COLORS.statusSuccess : AUTOPILOT_COLORS.statusDanger, fontWeight: 700 }}>
                        {r.ok ? "OK" : "Falha"}
                      </div>
                      <div style={{ wordBreak: "break-word" }}>{r.motivo || r.message || "-"}</div>
                    </div>
                  ))}
                </div>
              )}
            </>
          ) : null}
        </ModalBody>
      </Modal>
    </div>
  );
};

export default Importacao;
